<template>
  <span>
    <!-- Sem geometria selecionada, o material escolhido vira o fundo da célula -->
    <v-col v-if="isCellMaterial" class="pb-0">
      <p class="text-caption mb-1">Cell background material</p>
      <v-alert dense text type="info" class="text-caption mb-2 pa-2">
        No geometry is selected: clicking a material below changes the <b>background of the
        cell</b>. To change a geometry, select it first.
      </v-alert>
      <v-row align="center" no-gutters>
        <v-avatar size="24" class="mr-2" :color="cell.fill" style="border: 1px solid rgba(0, 0, 0, 0.3)" />
        <span class="text-body-2">{{ cell.materialTitle }} (&epsilon; = {{ cell.epsilon }})</span>
      </v-row>
      <v-btn
        v-if="!isDefaultCell"
        class="mt-2"
        small
        block
        outlined
        color="primary"
        @click="resetCellMaterial"
      >
        <v-icon left small>mdi-restore</v-icon>
        Reset to default (vacuum, &epsilon; = 1)
      </v-btn>
    </v-col>
    <v-col v-if="needsGeometry" class="pb-0">
      <v-alert dense text type="info" class="text-caption mb-0 pa-2">
        Select a geometry to choose its material.
      </v-alert>
    </v-col>
    <v-col>
      <v-row align="center" justify="center">
        <v-col cols="10">
          <v-text-field
            v-model="searchMaterialTitle"
            label="Search material"
            prepend-inner-icon="mdi-magnify"
            clearable
            hide-details
          />
        </v-col>

        <v-col cols="2">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                small
                fab
                link
                color="primary"
                v-on="on"
                @click="setMaterialForm(true)"
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </template>
            <span>Add Material</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </v-col>

    <v-list
      two-line
      color="theme-background"
      class="mt-0 pt-0"
      max-height="300px"
      style="overflow-y:scroll"
    >
      <template v-for="(group, groupIndex) in groups">
        <v-subheader :key="'header-' + groupIndex">{{ group.title }}</v-subheader>
        <template v-for="item in group.items">
          <v-list-item
            :key="item._id"
            link
            :input-value="isCurrent(item)"
            color="primary"
            @click="setMaterialOfCurrentGeometry(item)"
          >
            <v-list-item-avatar :color="item.color" size="32" style="border: 1px solid rgba(0, 0, 0, 0.3)">
              <span class="text-caption" style="color: #000a">{{ item.title[0] }}</span>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
              <v-list-item-subtitle>
                n = {{ item.refraction_index }} · ε = {{ epsilonOf(item) }}
                <span v-if="item.description"> · {{ item.description }}</span>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </template>
      </template>
      <v-list-item v-if="!groups.length">
        <v-list-item-content>
          <v-list-item-subtitle>No material matches “{{ searchMaterialTitle }}”.</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </v-list>
  </span>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import materials from "@/services/materials";
export default {
  name: "MaterialBox",
  computed: {
    ...mapGetters("simulator", ["getUserMaterials", "selectedToken", "cell", "mode"]),
    // O fundo da célula só existe no 2D (o 3D não envia default_material ao NGSolve)
    isCellMaterial() {
      return (
        this.selectedToken === "" &&
        this.mode !== "miniCanvas" &&
        !this.$store.state.simulator.is3d
      );
    },
    // Materiais prontos (todos os usuários) e os criados pelo usuário, filtrados pela busca
    groups() {
      const query = (this.searchMaterialTitle || "").trim().toLowerCase();
      const matches = (item) => !query ||
        `${item.title} ${item.description || ""} ${item.refraction_index}`.toLowerCase().includes(query);
      const items = (this.getUserMaterials || []).filter(matches);
      return [
        { title: "Ready-to-use materials", items: items.filter(item => item.builtin) },
        { title: "Your materials", items: items.filter(item => !item.builtin) }
      ].filter(group => group.items.length);
    },
    // 3D sem geometria selecionada: não há fundo para trocar
    needsGeometry() {
      return this.selectedToken === "" && this.mode !== "miniCanvas" && this.$store.state.simulator.is3d;
    },
    isDefaultCell() {
      return Number(this.cell.epsilon) === 1 && this.cell.fill === "#ffffff";
    }
  },
  methods: {
    epsilonOf(item) {
      const n = Number(item.refraction_index);
      return Number((n * n).toFixed(4));
    },
    isCurrent(item) {
      if (this.isCellMaterial) return this.cell.material_id === item._id;
      const data = this.$store.getters["simulator/geometryData"];
      return !!data && data.materialId === item._id;
    },
    ...mapActions("simulator", [
      "setMaterialForm",
      "setUserMaterials",
      "setMaterialOfCurrentGeometry",
      "resetCellMaterial"
    ])
  },
  data: () => ({
    searchMaterialTitle: ""
  }),
  mounted() {
    const user = window.firebase.auth().currentUser;
    materials
      .get("/", { params: { author: user ? user.uid : "" } })
      .then(({ data }) => {
        if (Array.isArray(data)) this.setUserMaterials(data);
      })
      .catch(() => {});
  }
};
</script>
<style lang="scss">
@import "./style.scss";
</style>
